import React from 'react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Sparkles, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

/**
 * Collapsible AI panel that docks at the top-right of the dashboard on
 * desktop (or full-width on mobile). Collapsed: a compact pill. Expanded:
 * pushes content down (no modal), with tabs for Morning briefing and
 * Weekly wrap. Pure presentation — data + generation logic stay unchanged.
 */
const AIBriefingPanel = ({
  narrative, narrativeLoading, narrativeRefreshing, onRefreshNarrative,
  weeklyWrap, weeklyLoading,
  expanded, onExpandedChange,
}) => {
  const setExpanded = (v) => { if (onExpandedChange) onExpandedChange(v); };
  const hasWeekly = weeklyLoading || Boolean(weeklyWrap?.wrap);

  const morningBody = (() => {
    if (narrativeLoading) {
      return (
        <div className="space-y-2 animate-pulse" data-testid="narrative-loading">
          <div className="h-3 bg-slate-200 rounded w-11/12" />
          <div className="h-3 bg-slate-200 rounded w-10/12" />
          <div className="h-3 bg-slate-200 rounded w-9/12" />
          <div className="h-3 bg-slate-200 rounded w-11/12 mt-3" />
          <div className="h-3 bg-slate-200 rounded w-8/12" />
        </div>
      );
    }
    if (narrative?.narrative) {
      return (
        <div
          className="text-[14px] leading-relaxed text-slate-800 whitespace-pre-line flex-1 min-h-0 overflow-y-auto pr-1"
          style={{ fontFamily: 'IBM Plex Sans' }}
          data-testid="narrative-text"
        >
          {narrative.narrative}
        </div>
      );
    }
    return (
      <p className="text-sm text-slate-500" data-testid="narrative-empty">
        {narrative?.error || 'Narrative unavailable right now. Your stats below are still up to date.'}
      </p>
    );
  })();

  const weeklyBody = (() => {
    if (weeklyLoading) {
      return (
        <div className="space-y-2 animate-pulse" data-testid="weekly-wrap-loading">
          <div className="h-3 bg-emerald-100 rounded w-11/12" />
          <div className="h-3 bg-emerald-100 rounded w-10/12" />
          <div className="h-3 bg-emerald-100 rounded w-9/12" />
        </div>
      );
    }
    if (weeklyWrap?.wrap) {
      return (
        <div
          className="text-[14px] leading-relaxed text-slate-800 whitespace-pre-line flex-1 min-h-0 overflow-y-auto pr-1"
          style={{ fontFamily: 'IBM Plex Sans' }}
          data-testid="weekly-wrap-text"
        >
          {weeklyWrap.wrap}
        </div>
      );
    }
    return <p className="text-sm text-slate-500">Weekly wrap will appear here on Sunday.</p>;
  })();

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="w-full lg:w-auto lg:ml-auto flex items-center gap-2 pl-3 pr-2 py-2 rounded-full border border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 text-indigo-900 shadow-sm transition-all"
        data-testid="ai-panel-pill"
      >
        <Sparkles className="h-4 w-4 text-indigo-700" />
        <span className="text-sm font-medium" style={{ fontFamily: 'Manrope' }}>Morning briefing and weekly wrap</span>
        <ChevronDown className="h-4 w-4 text-indigo-500 ml-1" />
      </button>
    );
  }

  return (
    <div
      className="w-full lg:w-[560px] lg:ml-auto rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-blue-50 shadow-md flex flex-col lg:self-stretch"
      data-testid="ai-panel-expanded"
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-700" />
          <span className="text-sm font-semibold text-indigo-900" style={{ fontFamily: 'Manrope' }}>AI briefing</span>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
          aria-label="Collapse"
          data-testid="ai-panel-collapse"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      </div>

      <Tabs defaultValue="morning" className="px-4 pb-4 flex-1 min-h-0 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <TabsList className="bg-white/70 border border-indigo-100">
            <TabsTrigger value="morning" data-testid="ai-tab-morning">Morning briefing</TabsTrigger>
            <TabsTrigger value="weekly" disabled={!hasWeekly} data-testid="ai-tab-weekly">
              Weekly wrap
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="morning" className="mt-0 flex-1 min-h-0 data-[state=active]:flex flex-col">
          <div className="flex items-center justify-between mb-2 flex-none">
            <p className="text-[11px] uppercase tracking-wider text-slate-500">
              Written by Claude · {narrative?.cached ? 'Cached from earlier today' : 'Fresh for today'}
            </p>
            <Button
              variant="ghost" size="sm"
              onClick={() => onRefreshNarrative(true)}
              disabled={narrativeRefreshing || narrativeLoading}
              data-testid="narrative-refresh-button"
              className="h-7 px-2 text-indigo-700 hover:text-indigo-900 hover:bg-indigo-100"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${narrativeRefreshing ? 'animate-spin' : ''}`} />
              {narrativeRefreshing ? 'Rewriting…' : 'Refresh'}
            </Button>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">{morningBody}</div>
        </TabsContent>

        <TabsContent value="weekly" className="mt-0 flex-1 min-h-0 data-[state=active]:flex flex-col" data-testid="weekly-wrap-card">
          {weeklyWrap?.snapshot?.week_start && (
            <p className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-2 inline-block flex-none">
              {weeklyWrap.snapshot.week_start} — {weeklyWrap.snapshot.week_end}
            </p>
          )}
          <div className="flex-1 min-h-0 flex flex-col">{weeklyBody}</div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIBriefingPanel;
